import { formatDate } from 'utils/format';
import { ScheduledStep, ScheduleStatus } from 'models/production-planning';

type PrintableStep = ScheduledStep & { totalProductionHours?: number };

type Translate = (key: string, options?: Record<string, unknown>) => string;

const escapeHtml = (value: string): string =>
    value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const statusLabel = (step: PrintableStep, translate: Translate): string => {
    if (step.scheduleStatus === ScheduleStatus.LATE) return translate('production-dashboard:STATUS_LATE');
    if (step.scheduleStatus === ScheduleStatus.EARLY) return translate('production-dashboard:STATUS_DONE');
    if (step.scheduleStatus === ScheduleStatus.PAUSED) return translate('production-dashboard:STATUS_PAUSED');
    return translate('production-dashboard:STATUS_ON_SCHEDULE');
};

const currentStepCell = (step: PrintableStep, translate: Translate): string => {
    const parts = [escapeHtml(step.currentStepName || '-')];
    if (step.isPaused) {
        parts.push(`<strong>(${escapeHtml(translate('production-dashboard:STATUS_PAUSED'))})</strong>`);
        if (step.pauseReason) parts.push(`<br/><small>${escapeHtml(step.pauseReason)}</small>`);
    }
    if (step.lastActionByName) {
        const actionKey =
            step.lastActionType === 'pause'
                ? 'PAUSED_BY'
                : step.lastActionType === 'resume'
                  ? 'RESUMED_BY'
                  : 'MOVED_BY';
        parts.push(
            `<br/><small>${escapeHtml(translate(`production-dashboard:${actionKey}`, { name: step.lastActionByName }))}</small>`,
        );
    }
    return parts.join(' ');
};

const customerCell = (step: PrintableStep): string => {
    const clinic = escapeHtml(step.deliveryClinicName || step.clinicName || '-');
    return step.dentistName ? `${clinic}<br/><small>${escapeHtml(step.dentistName)}</small>` : clinic;
};

const productionHoursCell = (step: PrintableStep): string => {
    const hours = step.totalProductionHours || step.estimatedProductionHours;
    return hours ? `${hours.toFixed(2)}h` : '-';
};

const buildRow = (step: PrintableStep, translate: Translate): string => `
    <tr>
        <td>${escapeHtml(step.orderDisplayId || step.orderId)}</td>
        <td>${currentStepCell(step, translate)}</td>
        <td>${customerCell(step)}</td>
        <td>${escapeHtml(step.patientName || '-')}</td>
        <td>${productionHoursCell(step)}</td>
        <td>${step.startDate ? formatDate(new Date(step.startDate), 'datetime') : '-'}</td>
        <td>${formatDate(new Date(step.deadline), 'datetime')}</td>
        <td>${escapeHtml(statusLabel(step, translate))}</td>
    </tr>`;

export const printOrdersList = (
    steps: PrintableStep[],
    departmentName: string,
    date: Date,
    translate: Translate,
): void => {
    const headers = [
        'ORDER_ID',
        'CURRENT_STEP',
        'CUSTOMER',
        'PATIENT',
        'PRODUCTION_HOURS',
        'START_DATE',
        'DEADLINE',
        'STATUS',
    ]
        .map((key) => `<th>${escapeHtml(translate(`production-dashboard:${key}`))}</th>`)
        .join('');

    const title = `${escapeHtml(departmentName)} - ${formatDate(date, 'date')}`;
    const html = `<!DOCTYPE html>
<html>
<head>
    <title>${title}</title>
    <style>
        body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin: 24px; }
        h1 { font-size: 16px; margin-bottom: 4px; }
        h2 { font-size: 13px; font-weight: normal; color: #555; margin-top: 0; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; }
        th, td { border: 1px solid #ccc; padding: 4px 8px; text-align: left; vertical-align: top; }
        th { background: #f0f0f0; }
        small { color: #555; }
    </style>
</head>
<body>
    <h1>${title}</h1>
    <h2>${escapeHtml(translate('production-dashboard:ORDERS_FOR_DAY'))}</h2>
    <table>
        <thead><tr>${headers}</tr></thead>
        <tbody>${steps.map((step) => buildRow(step, translate)).join('')}</tbody>
    </table>
</body>
</html>`;

    const printWindow = window.open('', 'PRINT', 'height=800,width=1000');
    if (!printWindow) return;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
};
